import { SxProps, Theme } from '@mui/material'
import { useId } from 'react'

// ----------------------------------------------------------------------
// A control that cannot be used states why, and the two are wired together.
//
// A dimmed control with no explanation is a dead end: the operator cannot
// tell whether they are missing a step, lack a permission, or are looking at
// a bug, and the only way to find out is to ask someone. Greying it out
// communicates that clicking will not work, which they had already worked
// out.
//
// Two things this gets right that are easy to get wrong by hand.
//
// It is `aria-disabled`, not `disabled`. A disabled control leaves the tab
// order, so the reason attached to it is announced to nobody: exactly the
// dead end a `title` tooltip is, which is what this rule replaces. The
// control stays focusable and its click is a no-op.
//
// And the id is per instance. Two of the same blocked control on one screen
// with a module-level constant id both point at the first one's sentence.
// ----------------------------------------------------------------------

/**
 * The muted presentation of a button that is present but cannot be pressed.
 *
 * **The same as a disabled button: the grey fill and no edge.** It used to
 * carry a `controlEdge` ring, drawn inset because a `contained` button has no
 * border for a colour to reach, on the rule that a control that cannot be used
 * keeps its boundary. On a button that read as a second look for the same
 * state: Pricing's Save, natively disabled, is the fill alone, and Import
 * from Tesla's Import vehicles, blocked the same way, wore an outline beside
 * it (reported on 2026-09-29: "Can we remove the border from Import vehicles
 * in disabled state?"). Every control this is spread onto is a button, and a
 * button's fill is its boundary. A switch, a checkbox and a field keep their
 * `controlEdge` edge when they cannot be used, because there the edge is the
 * control.
 *
 * The edge is `transparent` rather than removed, so an `outlined` button keeps
 * the border box it already has and does not shift a pixel becoming blocked.
 * Not the fill's colour: the fill is a translucent grey that paints under the
 * border too, so an edge of the same colour is that grey twice, a hairline
 * darker than the fill. The hover restates both, as `outlinedError` does: MUI
 * composes the general `outlined` hover after this, and it would otherwise
 * draw an edge and a surface wash under a pointer that cannot press anything.
 */
const blockedSx: SxProps<Theme> = {
  'color': (theme) => theme.palette.action.disabled,
  'backgroundColor': (theme) => theme.palette.action.disabledBackground,
  'borderColor': 'transparent',
  'boxShadow': 'none',
  'cursor': 'not-allowed',
  '&:hover': {
    backgroundColor: (theme) => theme.palette.action.disabledBackground,
    borderColor: 'transparent',
    boxShadow: 'none',
  },
}

export interface BlockedAction<T> {
  /** Spread onto the control. Carries the state, the wiring and the look. */
  controlProps: {
    'aria-disabled': boolean
    'aria-describedby': string | undefined
    'onClick': T | undefined
    'sx': SxProps<Theme> | undefined
  }
  /** Put on the element holding the sentence, so the control can point at it. */
  reasonProps: { id: string } | Record<string, never>
  /** Whether the control is currently blocked, for choosing its label. */
  blocked: boolean
}

/**
 * @param blocked whether the action cannot be taken right now
 * @param onClick what it does when it can
 */
export function useBlockedAction<T>(
  blocked: boolean,
  onClick: T,
): BlockedAction<T> {
  const id = useId()
  return {
    controlProps: {
      'aria-disabled': blocked,
      'aria-describedby': blocked ? id : undefined,
      // Not redundant with `aria-disabled`, which is only an announcement.
      // MUI hands `onClick` straight to the root for any consumer that is not
      // a native `<button>`, so withholding it is the only thing that stops
      // the action there.
      'onClick': blocked ? undefined : onClick,
      'sx': blocked ? blockedSx : undefined,
    },
    reasonProps: blocked ? { id } : {},
    blocked,
  }
}

export default useBlockedAction

/**
 * The reason wiring on its own, for a control that already handles being
 * unusable its own way.
 *
 * `useBlockedAction` is the better shape: it keeps the control focusable so
 * the reason can be reached by keyboard. But most of the console blocks a
 * control with the native `disabled`, and moving all of it is one deliberate
 * pass rather than a side effect of adding a sentence. Until then this
 * attaches the sentence without changing how the control blocks.
 */
export function useBlockedReason(blocked: boolean) {
  const id = useId()
  return {
    describedBy: blocked ? id : undefined,
    reasonProps: blocked ? { id } : {},
    blocked,
  }
}
